// src/components/facility-switcher.tsx
"use client";

import * as React from "react";
import { Building, Globe } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  useFacility,
  ALL_FACILITIES_ID,
  ALL_FACILITIES_NAME,
} from "@/contexts/FacilityContext";

export function FacilitySwitcher() {
  const { isMobile } = useSidebar();
  const {
    facilities,
    selectedFacilityId,
    selectFacility,
    isAllFacilities,
    selectedFacilityName,
    canUseAllFacilities,
  } = useFacility();

  // 現在選択中のアイコン
  const ActiveIcon = isAllFacilities ? Globe : Building;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              aria-label={`表示範囲: ${selectedFacilityName}。表示範囲を切り替える`}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <ActiveIcon className="size-4" />
              </div>
              <div className="min-w-0 flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {selectedFacilityName}
                </span>
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              表示範囲
            </DropdownMenuLabel>

            <DropdownMenuRadioGroup
              value={selectedFacilityId}
              onValueChange={selectFacility}
              aria-label="表示範囲"
            >
              {/* 組織全体（横断ビュー） */}
              {canUseAllFacilities && (
                <>
                  <DropdownMenuRadioItem
                    value={ALL_FACILITIES_ID}
                    className="min-h-11 gap-2 py-2 pr-2"
                  >
                    <div className="flex size-6 items-center justify-center rounded-sm border">
                      <Globe className="size-4 shrink-0" aria-hidden="true" />
                    </div>
                    <span className="min-w-0 flex-1 truncate">
                      {ALL_FACILITIES_NAME}
                    </span>
                  </DropdownMenuRadioItem>
                  <DropdownMenuSeparator />
                </>
              )}

              <DropdownMenuLabel className="text-muted-foreground text-xs">
                各施設
              </DropdownMenuLabel>

              {/* 個別施設リスト */}
              {facilities.map((facility) => (
                <DropdownMenuRadioItem
                  key={facility.id}
                  value={facility.id}
                  className="min-h-11 gap-2 py-2 pr-2"
                >
                  <div className="flex size-6 items-center justify-center rounded-sm border">
                    <Building className="size-4 shrink-0" aria-hidden="true" />
                  </div>
                  <span className="min-w-0 flex-1 truncate">
                    {facility.name}
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>

            {facilities.length === 0 && (
              <div className="text-muted-foreground p-2 text-xs">
                施設が見つかりません
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
